import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Shield, Heart, Sparkles, Plane, Star, Quote, Clock3, Award, BadgeCheck, Phone, Flower2, PawPrint, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import TourCard from "@/components/TourCard";
import InquiryDialog from "@/components/InquiryDialog";
import DestinationsSlider from "@/components/DestinationsSlider";
import SpecialCollections from "@/components/SpecialCollections";
import MockWeddingBanner from "@/components/MockWeddingBanner";
import AdvancedSearch from "@/components/AdvancedSearch";
import QuickEnquirySection from "@/components/QuickEnquirySection";
import Seo from "@/components/Seo";
import { organizationSchema, websiteSchema, faqSchema } from "@/components/JsonLd";
import { useSiteContent } from "@/context/SiteContentContext";
import RevealOnScroll from "@/components/RevealOnScroll";
import AnimatedNumber from "@/components/AnimatedNumber";

// Sunrise Taj Mahal — front view, reflection pool, vibrant peach/pink sky.
const HERO_IMG_DEFAULT =
  "https://images.pexels.com/photos/38263405/pexels-photo-38263405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const VALUE_PILLAR_ICONS = [Compass, Shield, Heart, Sparkles];

// Common pre-trip questions surfaced as Google FAQ rich results.
const HOME_FAQS = [
  {
    question: "What is the best time to visit India?",
    answer:
      "October to March is the most comfortable window across most of India — warm days, cool nights, and clear skies. April–June is peak Himalayan trekking season (Ladakh, Spiti, Kashmir). July–September brings the monsoon, magical for South India's tea estates and Kerala. We tailor every itinerary to the season you're travelling in.",
  },
  {
    question: "Do I need a visa for India?",
    answer:
      "Most international travellers can apply for an Indian e-Visa online at indianvisaonline.gov.in — it is usually issued within 72 hours and is valid for 30/60/90 days or 1 year. We send a step-by-step visa guide once you confirm your booking.",
  },
  {
    question: "How much does a typical India tour cost?",
    answer:
      "Our hand-crafted private tours start from USD 458 per person (twin share) for the 6-day Golden Triangle. Pricing varies with hotel category (3★/4★/5★/luxury), season, and group size. All quotes include guides, private vehicle, hotels, transfers and monument entry fees.",
  },
  {
    question: "Are flights included in the tour price?",
    answer:
      "International flights to India are NOT included by default — most of our guests book their own international ticket. Domestic flights inside India CAN be added on request. Internal trains and private vehicles are always included as per the itinerary.",
  },
  {
    question: "Can I combine India with Nepal, Dubai or Thailand?",
    answer:
      "Absolutely — that's one of our specialities. We craft seamless multi-country itineraries with India as the anchor, extending into Nepal (Himalaya, Kathmandu, Pokhara), the UAE (Dubai, Abu Dhabi), or Thailand (Bangkok, Phuket, Krabi). One single contract, one single point of contact.",
  },
  {
    question: "What is your cancellation policy?",
    answer:
      "Free cancellation up to 24 hours before departure for most bookings. Refunds are issued within 7–10 working days. Custom luxury tours and peak-season Diwali/Holi/New Year departures have separate terms — see /cancellation for the full policy.",
  },
];

export default function Home() {
  const [tours, setTours] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const c = useSiteContent("home");

  const STATS = [
    { num: c.stat_1_num, label: c.stat_1_label },
    { num: c.stat_2_num, label: c.stat_2_label },
    { num: c.stat_3_num, label: c.stat_3_label },
    { num: c.stat_4_num, label: c.stat_4_label },
  ];
  const VALUE_PILLARS = [1, 2, 3, 4].map((i) => ({
    icon: VALUE_PILLAR_ICONS[i - 1],
    title: c[`value_${i}_title`],
    text: c[`value_${i}_text`],
  }));

  useEffect(() => {
    (async () => {
      try {
        const [t, d, r] = await Promise.all([
          api.get("/tours", { params: { featured: true } }),
          api.get("/destinations"),
          api.get("/testimonials"),
        ]);
        setTours(t.data);
        setDestinations(d.data);
        setTestimonials(r.data);
      } catch (e) {
        console.warn("[Home] failed to load tours/destinations/testimonials:", e?.message || e);
      }
    })();
  }, []);

  const indianTours = tours.filter((t) =>
    ["golden-triangle", "spiritual", "diwali", "holi", "wildlife", "family"].includes(t.category)
  );

  return (
    <PageLayout>
      <Seo
        title="Crafted Journeys Through Incredible India"
        description="Hand-crafted journeys through Incredible India — Golden Triangle, Spiritual India, Rajasthan, Wildlife, Festivals, plus extensions to Nepal, Dubai and Thailand. Family-run, Agra-based."
        jsonLd={[organizationSchema(), websiteSchema(), faqSchema(HOME_FAQS)]}
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img
            src={c.hero_image || HERO_IMG_DEFAULT}
            alt="Taj Mahal at sunrise — Incredible India tours"
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/80" />
        </div>
        <div className="relative container-tti pb-24 pt-32 z-10">
          <div className="max-w-4xl">
            <div className="eyebrow text-marigold mb-6 animate-fade-up">
              {c.hero_eyebrow}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] text-white leading-[0.95] tracking-tighter animate-fade-up">
              {c.hero_title} <span className="italic text-marigold">{c.hero_title_italic}</span>
            </h1>
            <p
              className="text-white/85 text-lg md:text-xl max-w-2xl mt-7 font-light leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.15s" }}
            >
              {c.hero_subtitle}
            </p>
            <div
              className="flex flex-wrap gap-4 mt-10 animate-fade-up"
              style={{ animationDelay: "0.3s" }}
            >
              <button
                onClick={() => setInquiryOpen(true)}
                className="btn-primary hover:shadow-xl hover:shadow-deepred/40 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-cta-plan"
              >
                {c.hero_cta_primary || "Plan My Journey"} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/tours" className="inline-flex items-center px-7 py-3.5 rounded-sm border border-white/40 text-white text-sm uppercase tracking-wider font-medium hover:bg-white hover:text-deepred hover:border-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-cta-tours">
                {c.hero_cta_secondary || "Browse Tours"}
              </Link>
            </div>

            {/* ADVANCED SEARCH */}
            <div className="mt-12 animate-fade-up" style={{ animationDelay: "0.45s" }}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-marigold mb-3">Find your journey</div>
              <AdvancedSearch />
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/20 border border-white/20 backdrop-blur-md">
            {STATS.map((s, i) => (
              <div key={i} className="bg-black/30 px-6 py-6 text-center" data-testid={`stat-${i}`}>
                <div className="font-serif text-3xl md:text-4xl text-marigold">
                  <AnimatedNumber value={s.num} duration={1500 + i * 200} data-testid={`stat-num-${i}`} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESTINATIONS SLIDER — India · Nepal · Dubai · Thailand + Photographic Journey */}
      <DestinationsSlider />

      {/* THEMES BAND — Explore by theme */}
      <section className="py-20 md:py-28 bg-cream border-y border-cream-200" data-testid="themes-band">
        <div className="container-tti">
          <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
            <RevealOnScroll>
              <div className="eyebrow mb-4">Explore by theme</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Travel for what <span className="italic text-deepred">moves you</span>
              </h2>
              <p className="mt-5 text-foreground/70 text-lg font-light leading-relaxed">
                Handpicked journeys built around a passion — sunrise yoga, tiger safaris or the roar of Holi colours.
              </p>
            </RevealOnScroll>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                slug: "yoga",
                Icon: Flower2,
                eyebrow: "Yoga & Wellness",
                title: "Find your still centre",
                desc: "Rishikesh sadhana, Kerala Ayurveda, Mysore Ashtanga.",
                bg: "https://images.pexels.com/photos/6148088/pexels-photo-6148088.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
              },
              {
                slug: "culture",
                Icon: Landmark,
                eyebrow: "Culture & Temples",
                title: "Walk through millennia",
                desc: "Akshardham, Khajuraho, Meenakshi and heritage havelis.",
                bg: "https://images.pexels.com/photos/14907353/pexels-photo-14907353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
              },
              {
                slug: "festivals",
                Icon: Sparkles,
                eyebrow: "Festivals of India",
                title: "Live the celebration",
                desc: "Holi, Diwali, Pushkar Fair, Dev Deepawali.",
                bg: "https://images.pexels.com/photos/11511275/pexels-photo-11511275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
              },
              {
                slug: "wildlife",
                Icon: PawPrint,
                eyebrow: "Wildlife Safaris",
                title: "Track the striped king",
                desc: "Bengal tigers, rhinos and 500+ bird species.",
                bg: "https://images.unsplash.com/photo-1508817628294-5a453fa0b8fb?auto=format&fit=crop&q=85&w=1200&h=800",
              },
            ].map(({ slug, Icon, eyebrow, title, desc, bg }) => (
              <Link
                key={slug}
                to={`/themes/${slug}`}
                data-testid={`home-theme-card-${slug}`}
                className="group relative overflow-hidden rounded-sm aspect-[4/5] md:aspect-[4/3] block"
              >
                <img
                  src={bg}
                  alt={`${eyebrow} — ${title}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
                <div className="relative z-10 h-full flex flex-col justify-end p-7 md:p-8 text-white">
                  <div className="w-12 h-12 rounded-sm bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-marigold" strokeWidth={1.75} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-marigold mb-2">{eyebrow}</div>
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight mb-2">{title}</h3>
                  <p className="text-white/75 text-sm font-light leading-relaxed mb-5">{desc}</p>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white group-hover:text-marigold transition-colors">
                    Discover journeys <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MOCK WEDDING SIGNATURE BANNER */}
      <MockWeddingBanner />

      {/* SPECIAL UNIQUE COLLECTIONS */}
      <SpecialCollections />

      {/* VALUE PILLARS */}
      <section className="py-24 md:py-32 relative" data-testid="value-pillars">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <RevealOnScroll>
                <div className="eyebrow mb-4">{c.value_eyebrow}</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  {c.value_title}
                  <br />
                  <span className="italic text-deepred">{c.value_title_italic}</span>
                </h2>
              </RevealOnScroll>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-foreground/75 text-lg leading-relaxed font-light">
                {c.value_intro}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream-200 border border-cream-200">
            {VALUE_PILLARS.map((p, i) => (
              <div key={i} className="bg-cream p-8 md:p-10 hover:bg-white transition-colors">
                <div className="w-12 h-12 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center mb-6">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed font-light">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDIA PHOTO GALLERY — merged into DestinationsSlider above */}

      {/* TOP DESTINATIONS GRID */}
      <section className="py-24 md:py-32 relative" data-testid="top-destinations">
        <div className="container-tti">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <RevealOnScroll>
                <div className="eyebrow mb-4">Top Destinations</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
                  Where will your <span className="italic text-deepred">journey</span> begin?
                </h2>
              </RevealOnScroll>
            </div>
            <Link to="/destinations" className="btn-ghost group" data-testid="view-all-destinations">
              All destinations <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {destinations.slice(0, 12).map((d) => (
              <Link
                key={d.slug}
                to={`/destinations/${d.slug}`}
                data-testid={`home-dest-${d.slug}`}
                className="group relative aspect-[3/4] overflow-hidden rounded-sm bg-black"
              >
                <img
                  src={d.image_url}
                  alt={d.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-marigold mb-1">{d.country}</div>
                  <h3 className="font-serif text-2xl text-white tracking-tight leading-tight">{d.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TOURS - BENTO */}
      <section className="py-24 md:py-32 bg-cream-100 relative" data-testid="featured-tours">
        <div className="container-tti">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <RevealOnScroll>
                <div className="eyebrow mb-4">Signature Journeys</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
                  Six ways to experience <span className="italic">India</span>.
                </h2>
              </RevealOnScroll>
            </div>
            <Link to="/tours" className="btn-ghost group" data-testid="view-all-tours">
              View all tours
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Bento grid */}
          {indianTours.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
              <div className="md:col-span-7"><TourCard tour={indianTours[0]} span="wide" /></div>
              <div className="md:col-span-5"><TourCard tour={indianTours[1]} /></div>
              <div className="md:col-span-4"><TourCard tour={indianTours[2]} /></div>
              <div className="md:col-span-4"><TourCard tour={indianTours[3] || indianTours[2]} /></div>
              <div className="md:col-span-4"><TourCard tour={indianTours[4] || indianTours[0]} /></div>
            </div>
          )}
        </div>
      </section>

      {/* EXTENSIONS */}
      <section className="py-24 md:py-32 bg-peacock text-white relative overflow-hidden" data-testid="extensions">
        <div className="absolute inset-0 mandala-bg pointer-events-none" />
        <div className="container-tti relative">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-6">
              <RevealOnScroll>
                <div className="eyebrow text-marigold mb-4">Beyond India</div>
                <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
                  Pair your India journey with a
                  <br />
                  <span className="italic text-marigold">neighborly escape.</span>
                </h2>
              </RevealOnScroll>
            </div>
            <div className="lg:col-span-6 lg:pl-16">
              <p className="text-white/80 text-lg leading-relaxed font-light">
                Combine your India tour with the Himalayan kingdom of Nepal, the futuristic glamour of Dubai, or
                Thailand&rsquo;s tropical beaches — all booked as one seamless trip.
              </p>
              <div className="flex items-center gap-3 mt-8">
                <Plane className="w-5 h-5 text-marigold" />
                <span className="text-sm text-white/85">Single itinerary · single point of contact</span>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.filter((t) => ["nepal", "dubai", "thailand"].includes(t.category)).map((t) => (
              <TourCard key={t.slug} tour={t} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — Promise Band */}
      <section className="py-24 md:py-32 bg-cream" data-testid="why-choose-us">
        <div className="container-tti">
          <div className="text-center mb-16">
            <div className="eyebrow mb-4">Our Promise</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Why travelers choose <span className="italic text-deepred">TTI Holidays</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BadgeCheck, title: "Best Price Guarantee", text: "Direct hotel & supplier rates — no middlemen, no hidden fees." },
              { icon: Clock3, title: "24×7 Assistance", text: "Call, mail or WhatsApp anytime — our team is on-call throughout your trip." },
              { icon: Award, title: "Award-Winning", text: "Tripadvisor Certificate of Excellence recipient. IATO member." },
              { icon: Phone, title: "Free Cancellation", text: "Cancel up to 24 hours before departure and receive a full refund." },
            ].map((p) => (
              <div key={p.title} className="bg-white border border-cream-200 rounded-sm p-7 hover:shadow-md transition-shadow" data-testid={`promise-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl mb-2">{p.title}</h3>
                <p className="text-sm text-foreground/65 font-light leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>

          {/* Travel partners strip */}
          <div className="mt-20 pt-12 border-t border-cream-200">
            <div className="text-center mb-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">Featured on / Partnered with</div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-foreground/55">
              <span className="font-serif text-2xl tracking-tight italic">TripAdvisor</span>
              <span className="font-serif text-2xl tracking-tight italic">Lonely Planet</span>
              <span className="font-serif text-2xl tracking-tight italic">Expedia</span>
              <span className="font-serif text-2xl tracking-tight italic">Viator</span>
              <span className="font-serif text-2xl tracking-tight italic">GetYourGuide</span>
              <span className="font-serif text-2xl tracking-tight italic">IATO</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — Backs the FAQPage JSON-LD with visible content (Google requirement) */}
      <section className="py-24 md:py-32 bg-cream-100" data-testid="home-faq">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <RevealOnScroll>
                <div className="eyebrow mb-4">Frequently Asked</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  The answers
                  <br />
                  <span className="italic text-deepred">before you ask.</span>
                </h2>
              </RevealOnScroll>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-foreground/75 text-lg font-light leading-relaxed">
                Six of the most common questions travellers ask us before booking. Have one we haven&rsquo;t answered?
                Drop us a line on WhatsApp — usually replied in under an hour.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
            {HOME_FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`}>
                <h3 className="font-serif text-xl text-deepred mb-2 leading-snug">{f.question}</h3>
                <p className="text-foreground/75 font-light leading-relaxed text-[15px]">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 relative" data-testid="testimonials-home">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <RevealOnScroll>
                <div className="eyebrow mb-4">Travelers&rsquo; Words</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  Stories that made it
                  <br />
                  <span className="italic text-deepred">back home.</span>
                </h2>
              </RevealOnScroll>
            </div>
            <div className="lg:col-span-7 flex items-end justify-start lg:justify-end">
              <div className="flex items-center gap-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-saffron text-saffron" />
                  ))}
                </div>
                <span className="text-sm text-foreground/70">4.9 / 5 · 520+ reviews</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <div
                key={t.id}
                className="bg-white border border-cream-200 rounded-sm p-8 hover:shadow-md transition-shadow"
                data-testid={`testimonial-${t.id}`}
              >
                <Quote className="w-7 h-7 text-saffron mb-5 opacity-60" />
                <p className="text-foreground/85 font-light leading-relaxed mb-6 text-[15px]">
                  {t.text.length > 200 ? t.text.slice(0, 200) + "…" : t.text}
                </p>
                <div className="flex items-center gap-3 pt-5 border-t border-cream-200">
                  <img src={t.avatar_url} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-foreground/60">{t.location} · {t.tour_name}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/testimonials" className="btn-ghost" data-testid="all-testimonials">
              Read all stories <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK ENQUIRY — embedded form, no popup/redirect needed */}
      <QuickEnquirySection />

      {/* CTA BAND */}
      <section className="py-24 md:py-32 bg-deepred text-white relative overflow-hidden" data-testid="home-cta">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${c.cta_image || "https://images.pexels.com/photos/19756404/pexels-photo-19756404.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container-tti relative text-center">
          <div className="eyebrow text-marigold mb-5">{c.cta_eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight leading-[0.95] max-w-3xl mx-auto">
            {c.cta_title} <span className="italic text-marigold">{c.cta_title_italic}</span> {c.cta_title_after}
          </h2>
          <p className="text-white/80 text-lg max-w-xl mx-auto mt-6 font-light">
            {c.cta_body}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button onClick={() => setInquiryOpen(true)} className="btn-primary" data-testid="footer-cta-plan">
              Start Planning <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <a
              href="https://wa.me/917017798524"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3.5 rounded-sm border border-white/50 text-white text-sm uppercase tracking-wider font-medium hover:bg-white hover:text-deepred transition-all"
              data-testid="footer-cta-whatsapp"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} defaultTour={selectedTour} />
    </PageLayout>
  );
}
